import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import {
  Loader2, FolderKanban, Upload, ClipboardList, Users, ArrowRight,
  ChevronLeft, Clock, Sparkles,
} from "lucide-react";

const SECTION_LABELS = {
  dashboard: "Dashboard",
  programs: "Programs",
  ingestion: "Ingestion",
  reporting: "Reporting",
  forms: "Forms",
  other: "Other",
};

const SECTION_COLORS = {
  dashboard: "#10B981",
  programs: "#0F172A",
  ingestion: "#F59E0B",
  reporting: "#3B82F6",
  forms: "#8B5CF6",
  other: "#94A3B8",
};

function fmtMs(ms) {
  if (!ms || ms < 1000) return "0m";
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hrs}h ${rem}m` : `${hrs}h`;
}

function fmtRelative(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function MetricTile({ label, value, sub, icon: Icon, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-900 text-white",
    emerald: "bg-emerald-50 border border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border border-amber-200 text-amber-900",
    blue: "bg-blue-50 border border-blue-200 text-blue-900",
  };
  return (
    <div className={`p-5 rounded-md ${tones[tone] || tones.slate}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider opacity-70">{label}</p>
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
      </div>
      <p className="font-heading text-3xl font-semibold mt-2">{value}</p>
      {sub && <p className="text-xs opacity-70 mt-1">{sub}</p>}
    </div>
  );
}

function TimeDonut({ data }) {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) {
    return (
      <div className="text-center py-12 text-sm text-slate-400" data-testid="time-donut-empty">
        No time-on-task data yet. Spend some time in the app and check back later.
      </div>
    );
  }
  const entries = Object.entries(data)
    .filter(([, ms]) => ms > 0)
    .sort((a, b) => b[1] - a[1]);
  let cumulative = 0;
  const radius = 70;
  const stroke = 22;
  const circumference = 2 * Math.PI * radius;
  return (
    <div className="flex items-center gap-6" data-testid="time-donut">
      <svg width="170" height="170" viewBox="0 0 170 170">
        <g transform="translate(85, 85) rotate(-90)">
          <circle r={radius} fill="none" stroke="#F1F5F9" strokeWidth={stroke} />
          {entries.map(([sec, ms]) => {
            const pct = ms / total;
            const offset = circumference * (1 - cumulative);
            const length = circumference * pct;
            cumulative += pct;
            return (
              <circle
                key={sec}
                r={radius}
                fill="none"
                stroke={SECTION_COLORS[sec] || "#94A3B8"}
                strokeWidth={stroke}
                strokeDasharray={`${length} ${circumference}`}
                strokeDashoffset={offset}
              />
            );
          })}
        </g>
        <text x="85" y="80" textAnchor="middle" className="font-heading" fill="#0F172A" style={{ fontSize: 22, fontWeight: 600 }}>
          {fmtMs(total)}
        </text>
        <text x="85" y="100" textAnchor="middle" fill="#64748B" style={{ fontSize: 11 }}>
          total
        </text>
      </svg>
      <ul className="space-y-2 text-sm">
        {entries.map(([sec, ms]) => (
          <li key={sec} className="flex items-center gap-2" data-testid={`time-row-${sec}`}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: SECTION_COLORS[sec] }} />
            <span className="text-slate-700 w-24">{SECTION_LABELS[sec] || sec}</span>
            <span className="font-mono text-slate-500 text-xs">{fmtMs(ms)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WeeklyTrend({ data }) {
  const maxV = Math.max(1, ...data.map((d) => d.rows + d.submissions));
  return (
    <div data-testid="weekly-trend">
      <div className="flex items-end justify-between gap-1 h-32">
        {data.map((d, i) => {
          const total = d.rows + d.submissions;
          const rowsH = (d.rows / maxV) * 100;
          const subH = (d.submissions / maxV) * 100;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex flex-col justify-end h-28" title={`${total} contributions`}>
                {d.submissions > 0 && <div className="bg-blue-400 rounded-t-sm" style={{ height: `${subH}%` }} />}
                {d.rows > 0 && <div className="bg-slate-900" style={{ height: `${rowsH}%` }} />}
              </div>
              <span className="text-[10px] text-slate-400">
                {new Date(d.week_start).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-slate-900" /> Rows</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-blue-400" /> Submissions</span>
      </div>
    </div>
  );
}

function TeamPanel({ user }) {
  const [programs, setPrograms] = useState([]);
  const [picked, setPicked] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/programs").then(({ data }) => {
      const owned = (data || []).filter((p) => p.owner_id === user.id);
      setPrograms(owned);
      if (owned.length > 0) setPicked(owned[0].id);
    }).catch(() => {});
  }, [user.id]);

  useEffect(() => {
    if (!picked) return;
    setLoading(true);
    api.get(`/me/analytics/team/${picked}`)
      .then(({ data }) => setTeam(data))
      .catch(() => setTeam(null))
      .finally(() => setLoading(false));
  }, [picked]);

  if (programs.length === 0) {
    return (
      <div className="text-sm text-slate-400 py-6 text-center" data-testid="team-empty">
        You don't own any programs yet. Create one to see team activity here.
      </div>
    );
  }

  return (
    <div data-testid="team-panel">
      <div className="flex items-center justify-between mb-4">
        <select
          className="text-sm border border-slate-200 rounded-sm px-3 py-1.5 bg-white text-slate-700 max-w-xs"
          value={picked || ""}
          onChange={(e) => setPicked(e.target.value)}
          data-testid="team-program-select"
        >
          {programs.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
      ) : team ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <th className="text-left py-2 font-medium">Member</th>
              <th className="text-left py-2 font-medium">Last active</th>
              <th className="text-right py-2 font-medium">Rows</th>
              <th className="text-right py-2 font-medium">Submissions</th>
            </tr>
          </thead>
          <tbody data-testid="team-rows">
            {team.team.map((m) => (
              <tr key={m.user_id} className="border-b border-slate-50 last:border-b-0">
                <td className="py-2.5">
                  <div className="text-slate-800">{m.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{m.email}{m.is_owner && <span className="ml-2 text-[10px] uppercase text-emerald-700">Owner</span>}</div>
                </td>
                <td className="py-2.5 text-slate-600 text-xs">{fmtRelative(m.last_active)}</td>
                <td className="py-2.5 text-right font-mono">{m.rows_contributed}</td>
                <td className="py-2.5 text-right font-mono">{m.submissions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

export default function MyAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/me/analytics?days=30")
      .then(({ data: d }) => setData(d))
      .catch((err) => setError(err.response?.data?.detail || "Couldn't load analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] grid place-items-center" data-testid="analytics-loading">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }
  if (error) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-10" data-testid="analytics-error">
          <p className="text-sm text-rose-700">{error}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="my-analytics-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-overline">My performance</p>
          <h1 className="font-heading text-4xl font-bold text-slate-900 mt-1">Your last 30 days</h1>
          <p className="text-sm text-slate-500 mt-2">Personalised analytics for {data.user.name}. Updates as you work.</p>
        </div>
        <span className="text-xs font-mono uppercase px-2 py-1 rounded-sm bg-slate-100 text-slate-600">
          {data.window_days}d window
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <MetricTile
          testid="kpi-active-programs"
          label="Active programs"
          value={data.active_programs_count}
          sub={`of ${data.total_programs_count} total`}
          icon={FolderKanban}
          tone="slate"
        />
        <MetricTile
          testid="kpi-uploads"
          label="Uploads"
          value={data.contributions.uploads}
          sub={`${data.contributions.rows_total} rows`}
          icon={Upload}
          tone="emerald"
        />
        <MetricTile
          testid="kpi-submissions"
          label="Submissions"
          value={data.contributions.form_submissions}
          sub="forms filled"
          icon={ClipboardList}
          tone="blue"
        />
        <MetricTile
          testid="kpi-time"
          label="Time on task"
          value={fmtMs(Object.values(data.time_on_task_ms).reduce((a, b) => a + b, 0))}
          sub="across all sections"
          icon={Clock}
          tone="amber"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <section className="bg-white border border-slate-200 rounded-md p-6">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-1">Time by section</h2>
          <p className="text-xs text-slate-500 mb-5">Where your time has been going.</p>
          <TimeDonut data={data.time_on_task_ms} />
        </section>
        <section className="bg-white border border-slate-200 rounded-md p-6">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-1">Weekly contributions</h2>
          <p className="text-xs text-slate-500 mb-5">Rows uploaded + form submissions, by week.</p>
          <WeeklyTrend data={data.weekly_trend} />
        </section>
      </div>

      <section className="bg-white border border-slate-200 rounded-md p-6 mb-10">
        <h2 className="font-heading text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-600" /> Your programs
        </h2>
        <p className="text-xs text-slate-500 mb-5">Programs you own or collaborate on.</p>
        {data.active_programs.length === 0 ? (
          <p className="text-sm text-slate-400 py-4">No programs yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="my-programs-list">
            {data.active_programs.map((p) => (
              <li key={p.id} className="py-3 flex items-center justify-between">
                <div>
                  <Link to={`/programs/${p.id}`} className="text-sm font-medium text-slate-800 hover:text-emerald-700">
                    {p.name}
                  </Link>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {p.is_owner ? "Owner" : "Collaborator"} · {p.activity_count} contribution{p.activity_count === 1 ? "" : "s"} this window
                  </div>
                </div>
                <Link to={`/programs/${p.id}/report`} className="text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1">
                  Open report <ArrowRight className="w-3 h-3" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {data.is_owner_of_any && (
        <section className="bg-white border border-slate-200 rounded-md p-6 mb-10" data-testid="team-activity-section">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
            <Users className="w-4 h-4 text-emerald-600" /> Team activity
          </h2>
          <p className="text-xs text-slate-500 mb-5">Last activity + contributions for each member of a program you own.</p>
          <TeamPanel user={data.user} />
        </section>
      )}

      <p className="text-xs text-slate-400 text-center">
        Activity tracking is anonymous to other users and used only to power this view + your Saturday email digest.
      </p>
      </div>
    </Layout>
  );
}
